import { supabase } from './supabase';
import { AvatarService } from './avatar-service';
import type { User, UserProfile } from './database.types';

export interface ProfileUpdateData {
  name?: string;
  email?: string;
  company?: string;
  job_title?: string;
  bio?: string;
  avatar?: string;
  tutorial_completed?: boolean;
  onboarding_completed?: boolean;
}

export interface CompleteUserProfile extends User {
  company?: string;
  job_title?: string;
  bio?: string;
  system_role?: string | null;
}

export interface PasswordUpdateData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export class ProfileService {
  /**
   * Get complete user profile including profile data
   */
  static async getUserProfile(userId: string): Promise<CompleteUserProfile | null> {
    try {
      // Fetch user data
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user:', userError);
        throw userError;
      }

      if (!user) {
        return null;
      }

      // Fetch profile data
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      // Note: Profile might not exist yet, so don't throw error if not found
      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching user profile:', profileError);
      }

      // Combine user and profile data
      const completeProfile: CompleteUserProfile = {
        ...user,
        company: profile?.company || undefined,
        job_title: profile?.job_title || undefined,
        bio: profile?.bio || undefined
      };

      return completeProfile;
    } catch (error) {
      console.error('ProfileService.getUserProfile error:', error);
      return null;
    }
  }

  /**
   * Initialize a new user profile in the database
   */
  static async initializeProfile(userId: string, userData: { email: string; name: string; avatar?: string | null }): Promise<CompleteUserProfile> {
    try {
      // Create user record in users table
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .insert({
          id: userId,
          email: userData.email,
          name: userData.name,
          avatar: userData.avatar,
          tutorial_completed: false,
          onboarding_completed: false,
          subscription_plan: null,
          organization_id: null,
          team_role: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (userError) {
        throw new Error(`Failed to create user: ${userError.message}`);
      }

      // Return the new user as CompleteUserProfile
      const completeProfile: CompleteUserProfile = {
        ...newUser,
        company: undefined,
        job_title: undefined,
        bio: undefined
      };

      return completeProfile;
    } catch (error) {
      console.error('ProfileService.initializeProfile error:', error);
      throw error;
    }
  }

  /**
   * Update user profile information (handles both users and user_profiles tables)
   */
  static async updateProfile(userId: string, updates: ProfileUpdateData): Promise<CompleteUserProfile> {
    try {
      // Validate required fields
      if (!userId) {
        throw new Error('User ID is required');
      }

      // If email is being updated, we need to update both Supabase Auth and database
      if (updates.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: updates.email
        });

        if (authError) {
          throw new Error(`Failed to update email: ${authError.message}`);
        }
      }

      // Separate updates for users table and profiles table
      const userUpdates: Partial<User> = {};
      const profileUpdates: Partial<UserProfile> = {};

      // Distribute updates to appropriate tables
      if (updates.name !== undefined) userUpdates.name = updates.name;
      if (updates.email !== undefined) userUpdates.email = updates.email;
      if (updates.avatar !== undefined) userUpdates.avatar = updates.avatar;
      if (updates.tutorial_completed !== undefined) userUpdates.tutorial_completed = updates.tutorial_completed;
      if (updates.onboarding_completed !== undefined) userUpdates.onboarding_completed = updates.onboarding_completed;
      if (updates.company !== undefined) profileUpdates.company = updates.company;
      if (updates.job_title !== undefined) profileUpdates.job_title = updates.job_title;
      if (updates.bio !== undefined) profileUpdates.bio = updates.bio;

      // Update users table if needed
      if (Object.keys(userUpdates).length > 0) {
        const { error: userError } = await supabase
          .from('users')
          .update({
            ...userUpdates,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (userError) {
          throw new Error(`Failed to update user: ${userError.message}`);
        }
      }

      // Update or create profile record if needed
      if (Object.keys(profileUpdates).length > 0) {
        // First try to update existing profile
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            ...profileUpdates,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        // If no rows were affected, create new profile
        if (updateError && updateError.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: userId,
              ...profileUpdates,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) {
            throw new Error(`Failed to create user profile: ${insertError.message}`);
          }
        } else if (updateError) {
          throw new Error(`Failed to update user profile: ${updateError.message}`);
        }
      }

      // Return updated complete profile
      const updatedProfile = await this.getUserProfile(userId);
      if (!updatedProfile) {
        throw new Error('Failed to retrieve updated profile');
      }

      return updatedProfile;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  /**
   * Update user avatar
   */
  static async updateAvatar(userId: string, file: File, currentAvatarPath?: string): Promise<string> {
    try {
      // Upload new avatar
      const { url, path } = await AvatarService.uploadAvatar(file, userId);

      // Update user's avatar in database
      const { error } = await supabase
        .from('users')
        .update({
          avatar: url,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        // If database update fails, try to clean up uploaded file
        await AvatarService.deleteAvatar(path);
        throw new Error(`Failed to update avatar: ${error.message}`);
      }

      // Delete old avatar if it exists and is not a default avatar
      if (currentAvatarPath && !currentAvatarPath.includes('data:image') && !currentAvatarPath.includes('pexels.com')) {
        await AvatarService.deleteAvatar(currentAvatarPath);
      }

      return url;
    } catch (error) {
      console.error('Avatar update error:', error);
      throw error;
    }
  }

  /**
   * Change user password
   */
  static async changePassword(passwordData: PasswordUpdateData): Promise<void> {
    try {
      // Validate password data
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        throw new Error('All password fields are required');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      if (passwordData.newPassword.length < 8) {
        throw new Error('New password must be at least 8 characters long');
      }

      if (passwordData.newPassword === passwordData.currentPassword) {
        throw new Error('New password must be different from current password');
      }

      // Update password using Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        throw new Error(`Failed to change password: ${error.message}`);
      }
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }

  /**
   * Delete user account and all associated data
   * Comprehensive deletion with Stripe cancellation and proper cleanup
   */
  static async deleteAccount(userId: string): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      console.log('🗑️ [DEBUG] Starting comprehensive account deletion for user:', userId);
      console.log('🗑️ [DEBUG] ProfileService.deleteAccount() called successfully');

      // Import stripe cancellation service dynamically to avoid circular imports
      const { StripeCancellationService } = await import('./stripe-cancellation-service');

      // Step 1: Cancel active Stripe subscription
      console.log('1️⃣ Canceling Stripe subscription...');
      const cancellationResult = await StripeCancellationService.cancelSubscription(userId);
      if (!cancellationResult.success) {
        console.warn('⚠️ Stripe cancellation warning:', cancellationResult.message);
        // Don't throw error - continue with deletion even if Stripe fails
      } else {
        console.log('✅ Stripe subscription handled:', cancellationResult.message);
      }

      // Step 2: Get user data for cleanup and audit
      const { data: userData } = await supabase
        .from('users')
        .select('avatar, email, name, subscription_plan')
        .eq('id', userId)
        .single();

      if (!userData) {
        throw new Error('User not found');
      }

      // Step 3: Handle database foreign key constraints (NO ACTION constraints)
      console.log('2️⃣ Handling foreign key constraints...');
      
      // Delete activity_log entries (NO ACTION constraint)
      await supabase
        .from('activity_log')
        .delete()
        .eq('user_id', userId);

      // Nullify blog_posts author_id (NO ACTION constraint) 
      await supabase
        .from('blog_posts')
        .update({ author_id: null })
        .eq('author_id', userId);

      // Delete team_members where this user invited someone (NO ACTION constraint)
      await supabase
        .from('team_members')
        .delete()
        .eq('invited_by', userId);

      console.log('✅ Foreign key constraints handled');

      // Step 4: Comprehensive storage cleanup
      console.log('3️⃣ Cleaning up storage files...');
      
      // Delete avatar from storage
      if (userData?.avatar && !userData.avatar.includes('data:image') && !userData.avatar.includes('pexels.com')) {
        try {
          const avatarPath = userData.avatar.split('/').slice(-3).join('/');
          await AvatarService.deleteAvatar(avatarPath);
          console.log('✅ Avatar deleted');
        } catch (avatarError) {
          console.warn('⚠️ Avatar deletion warning:', avatarError);
        }
      }

      // Delete from all storage buckets
      const storageBuckets = ['creatives', 'ad-generations', 'ad-generation-assets'];
      
      for (const bucket of storageBuckets) {
        try {
          const { data: files } = await supabase.storage
            .from(bucket)
            .list(userId, { limit: 1000 });

          if (files && files.length > 0) {
            const filePaths = files.map(file => `${userId}/${file.name}`);
            const { error } = await supabase.storage
              .from(bucket)
              .remove(filePaths);
            
            if (error) {
              console.warn(`⚠️ Warning cleaning ${bucket}:`, error);
            } else {
              console.log(`✅ Cleaned ${bucket}: ${files.length} files`);
            }
          }
        } catch (storageError) {
          console.warn(`⚠️ Error accessing ${bucket}:`, storageError);
        }
      }

      // Step 4.5: Remove from Resend marketing audience
      console.log('3.5️⃣ Removing from Resend marketing audience...');
      try {
        const { EmailService } = await import('./email-service');
        await EmailService.removeFromResend(userData.email, userId);
        console.log('✅ Removed from Resend marketing audience');
      } catch (resendError) {
        console.warn('⚠️ Resend removal warning:', resendError);
        // Don't throw - this is a best-effort cleanup
      }

      // Step 5: Create audit log entry
      console.log('4️⃣ Creating audit log...');
      try {
        await supabase.from('auth_audit_logs').insert({
          event_type: 'account_deleted',
          user_id: userId,
          metadata: {
            email: userData.email,
            name: userData.name,
            subscription_plan: userData.subscription_plan,
            deleted_at: new Date().toISOString(),
            deletion_method: 'user_requested',
            stripe_canceled: cancellationResult.success
          },
          ip_address: null,
          user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : null
        });
        console.log('✅ Audit log created');
      } catch (auditError) {
        console.warn('⚠️ Audit log warning:', auditError);
      }

      // Step 6: Send account deletion confirmation email
      console.log('5️⃣ Sending confirmation email...');
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            to: userData.email,
            subject: 'Account Deletion Confirmation',
            template: 'account-deleted',
            data: {
              userName: userData.name || userData.email.split('@')[0],
              deletionDate: new Date().toLocaleDateString(),
              supportEmail: 'support@rulevia.com'
            }
          }
        });
        console.log('✅ Confirmation email sent');
      } catch (emailError) {
        console.warn('⚠️ Email warning:', emailError);
        // Don't throw - email failure shouldn't block deletion
      }

      // Step 7: Delete Auth user and database user via secure edge function
      console.log('6️⃣ [DEBUG] About to call delete-account edge function...');
      console.log('6️⃣ [DEBUG] Calling supabase.functions.invoke with userId:', userId);
      
      const { data: deleteResult, error: deleteError } = await supabase.functions.invoke('delete-account', {
        body: {
          user_id: userId
        }
      });
      
      console.log('6️⃣ [DEBUG] Edge function response received:', { deleteResult, deleteError });

      if (deleteError || !deleteResult?.success) {
        const errorMessage = deleteError?.message || deleteResult?.message || 'Failed to delete account';
        console.error('❌ Account deletion failed:', errorMessage);
        throw new Error(`Account deletion failed: ${errorMessage}`);
      }

      console.log('✅ Auth user and database record deleted successfully:', deleteResult.details);
      console.log('🎉 Account deletion completed successfully');
      
      // Note: User logout is handled by the UI layer after this function completes
      // This ensures proper application state cleanup through the auth context

    } catch (error) {
      console.error('❌ Account deletion error:', error);
      throw new Error(`Account deletion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get user's current subscription info for profile display
   */
  static async getUserSubscriptionInfo(userId: string): Promise<{
    planName: string;
    credits: number;
    hasActiveSubscription: boolean;
  }> {
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('subscription_plan, credits')
        .eq('id', userId)
        .single();

      const planNames: Record<string, string> = {
        'free_trial': 'Free Trial',
        'starter': 'Starter',
        'pro': 'Pro',
        'agency': 'Agency',
        'enterprise': 'Enterprise'
      };

      return {
        planName: planNames[userData?.subscription_plan || 'free_trial'] || 'Free Trial',
        credits: userData?.credits || 0,
        hasActiveSubscription: userData?.subscription_plan !== null && userData?.subscription_plan !== 'free_trial'
      };
    } catch (error) {
      console.error('Error getting subscription info:', error);
      return {
        planName: 'Free Trial',
        credits: 0,
        hasActiveSubscription: false
      };
    }
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate name format
   */
  static isValidName(name: string): boolean {
    return name.trim().length >= 2 && name.trim().length <= 100;
  }
}

export default ProfileService;